import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Plus, Pencil, Trash2, GitMerge, Layers } from 'lucide-react';

const AdminMergeTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', doc_type_ids: [], template: '' });
  const [docSearch, setDocSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        api.get('/merge-templates'),
        api.get('/document-types'),
      ]);
      setTemplates(t.data);
      setDocTypes(d.data);
    } catch {
      toast.error('Failed to load merge templates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dtName = (id) => docTypes.find((d) => d.id === id)?.name || 'Unknown';

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', doc_type_ids: [], template: '' });
    setDocSearch('');
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, doc_type_ids: [...t.doc_type_ids], template: t.template });
    setDocSearch('');
    setDialogOpen(true);
  };

  const toggleDoc = (id) => {
    setForm((f) => ({
      ...f,
      doc_type_ids: f.doc_type_ids.includes(id)
        ? f.doc_type_ids.filter((x) => x !== id)
        : [...f.doc_type_ids, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (form.doc_type_ids.length < 2) return toast.error('Select at least 2 document types');
    if (!form.template.trim()) return toast.error('Template text is required');
    setSubmitting(true);
    try {
      if (editing) await api.put(`/merge-templates/${editing.id}`, form);
      else await api.post('/merge-templates', form);
      toast.success(editing ? 'Merge template updated' : 'Merge template created');
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete merge template "${t.name}"?`)) return;
    try {
      await api.delete(`/merge-templates/${t.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const filteredDocTypes = docTypes.filter((d) =>
    d.name.toLowerCase().includes(docSearch.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="admin-merge-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Merge Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Combine two or more documents (e.g. RTC + MR + EC) into a single Tracing of Title paragraph.
          </p>
        </div>
        <Button className="bg-[#1e3a5f] hover:bg-[#162c46]" onClick={openCreate} data-testid="add-merge-template-btn">
          <Plus size={18} className="mr-2" /> Add Merge Template
        </Button>
      </div>

      {loading ? (
        <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <GitMerge size={32} className="mx-auto mb-3 text-slate-300" />
            No merge templates yet. Create one to enable merging documents in the Tracing of Title section.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {templates.map((t) => (
            <Card key={t.id} data-testid={`merge-template-${t.id}`}>
              <CardContent className="py-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{t.name}</span>
                    {t.doc_type_ids.map((id) => (
                      <span key={id} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {dtName(id)}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-1.5 line-clamp-2">{t.template}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-merge-${t.id}`}>
                    <Pencil size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(t)} data-testid={`delete-merge-${t.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="merge-template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Merge Template' : 'Add Merge Template'}</DialogTitle>
            <DialogDescription>
              Select the exact set of document types this merge applies to, and write the combined paragraph.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. RTC + MR + EC" data-testid="merge-name-input" />
            </div>

            <div className="space-y-2">
              <Label>Document Types (select 2 or more) *</Label>
              <Input value={docSearch} onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Search document types..." className="mb-1" data-testid="merge-doc-search" />
              <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                {filteredDocTypes.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm">
                    <Checkbox checked={form.doc_type_ids.includes(d.id)} onCheckedChange={() => toggleDoc(d.id)}
                      data-testid={`merge-doc-${d.id}`} />
                    <span>{d.name}</span>
                    <span className="text-xs text-slate-400 ml-auto">{d.category}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400">{form.doc_type_ids.length} selected</p>
            </div>

            <div className="space-y-2">
              <Label>Merged Paragraph Template *</Label>
              <Textarea rows={5} value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })}
                placeholder="Use {doc[1].field}, {doc[2].field} for each document's fields (in furnished order), {trace_doc_no} for the combined document numbers, and {field} for the first document."
                data-testid="merge-template-text" />
              <div className="text-xs text-slate-400 flex items-start gap-1.5">
                <Layers size={13} className="mt-0.5 shrink-0" />
                <span>
                  Placeholders: <code className="text-[#1e3a5f]">{'{doc[1].survey_number}'}</code>, <code className="text-[#1e3a5f]">{'{doc[2].date}'}</code> (per document in order),
                  <code className="text-[#1e3a5f]"> {'{trace_doc_no}'}</code> (combined doc numbers), and plain <code className="text-[#1e3a5f]">{'{village}'}</code> (from the first document).
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#162c46]" disabled={submitting} data-testid="save-merge-template-btn">
                {submitting ? 'Saving…' : editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMergeTemplatesPage;
